/**
 * URL → page mapping for the unified capture + execute PWA.
 *
 *   /q/<token>        → CapturePage (contribute / QR report)
 *   /<token>          → CapturePage (bare-token shortcut)
 *   /t/<shortId>      → TaskPage (execute)
 *   /f/<shortId>      → TaskPage (follow / awareness)
 *   /r/<shortId>      → RunPage (external execute)
 *   /r/run_<id>       → RunPage (legacy Remind / PDF URL; same assignee gate)
 *   /                 → HomePage (the front door, signed in or not)
 *   /home             → HomePage (legacy alias; kept for links already shared)
 *   /home/t/<taskId>  → HomeTaskPage (assigned task opened by id, not link)
 *   /home/sent/<taskId> → ContributedTaskPage (a report you sent to someone else)
 *   /home/r/<runId>   → HomeRunPage (assigned run opened by id, not link)
 *   /home/list/<key>  → WorkListPage (one status category)
 *   /routines         → RoutinesPage (owner's routine list + create)
 *   /home/routines    → same (legacy alias)
 *   /buckets          → BucketsPage (footer tab: Pastas)
 *   /contacts         → ContactsPage (footer tab: Contatos)
 *   /find             → FindPage (footer tab: Achar)
 *   /activity         → ActivityPage (header pulse button)
 *   /settings         → SettingsPage (header settings button)
 *   /settings/<name>  → SettingsPage with one section open (native's sub-screens)
 *   /design           → DesignLabPage (Hwyah 9999 only; others see not-found)
 *   ?token=           → CapturePage (legacy query form)
 *
 * Legacy: awareness links that were shared as /t/<id> still resolve via
 * link.purpose on the server (TaskPage calls resolveTaskForWeb).
 *
 * Removed: `/track/<shortId>`, a standalone page for a contributor to watch one
 * report. The home does that job for every report at once, under Contributed, so
 * a page outside the app was a worse answer to a question the app can answer.
 * Nothing about it survives — not the route, not the reserved prefix. A link
 * shared before it went away has a second path segment, so it can never be read
 * as a bare capture token; it falls through to the home, which is where the
 * report now is.
 */
import { RESERVED_PATH_PREFIXES } from './config/webOrigins';
import {
  SETTINGS_SECTIONS,
  type SettingsSection,
} from './home/settings/sections';

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export type Route =
  | { kind: 'capture'; token: string }
  | { kind: 'task'; shortId: string }
  | { kind: 'follow'; shortId: string }
  | { kind: 'runExecute'; shortId: string }
  | { kind: 'home' }
  // `shortId` is the entity's already-known execute link, passed as `?s=` when
  // the home has it. It turns opening into a straight render instead of a
  // round trip that trades the id for a link behind a spinner.
  | { kind: 'homeTask'; taskId: string; shortId: string | null }
  /**
   * A report you filed into somebody else's bucket. Its own route rather than a
   * mode of `homeTask`, because the two differ in the one thing the router
   * decides: this is a *reading* surface with nothing to do on it, so it admits
   * desktop, while `/home/t/` keeps the handoff QR because executing needs a
   * camera. `HomeTaskPage` redirects here when it finds a contributor.
   */
  | { kind: 'homeSent'; taskId: string }
  | { kind: 'homeRun'; runId: string; shortId: string | null }
  | { kind: 'homeList'; category: string }
  /** Whose work the board is showing (D26) — `all` is the home. */
  | { kind: 'homeLens'; lens: string }
  // The owner's own furniture (D24). `collection` is what the page groups by;
  // the three read differently enough to be told apart in the URL.
  | { kind: 'homeBucket'; bucketId: string }
  | { kind: 'homeRoutine'; bucketId: string }
  | { kind: 'homeRoutines' }
  | { kind: 'homeContact'; contactId: string }
  // The phone footer's own destinations (native `BottomNavigation`
  // `taskListFilters`), plus the two header buttons. Every one of these is a
  // top-level path, so each had to be reserved in `RESERVED_PATH_PREFIXES` or
  // the bare-token fallback below would read it as a QR token.
  | { kind: 'homeBuckets' }
  | { kind: 'homeContacts' }
  | { kind: 'homeFind' }
  // `/activity`, not `/history`: `history` is already a billing segment that
  // resolves to the account page, and native calls this screen Atividade.
  | { kind: 'homeActivity' }
  /**
   * `section` is null on the hub. Native gives every settings row its own
   * screen; the web keeps those as addressable paths so a phone can drill in
   * and a desktop can deep-link a panel. An unknown name is the hub, not a
   * dead end.
   */
  | { kind: 'homeSettings'; section: SettingsSection | null }
  /** Private component catalog. The page itself refuses anyone but 9999. */
  | { kind: 'designLab' };

export function parseRoute(pathname: string, search: string): Route {
  const trimmed = pathname.replace(/^\/+|\/+$/g, '');

  // Segment-aware, not `startsWith('home')` — a QR token may legitimately
  // begin with those letters, and only the exact first segment is reserved.
  if (trimmed === 'home' || trimmed.startsWith('home/')) {
    const knownShortId = new URLSearchParams(search).get('s') || null;
    if (trimmed.startsWith('home/t/')) {
      const taskId = trimmed.slice('home/t/'.length).split('/')[0];
      if (taskId) {
        return { kind: 'homeTask', taskId: decodeSegment(taskId), shortId: knownShortId };
      }
    }
    if (trimmed.startsWith('home/sent/')) {
      const taskId = trimmed.slice('home/sent/'.length).split('/')[0];
      if (taskId) return { kind: 'homeSent', taskId: decodeSegment(taskId) };
    }
    if (trimmed.startsWith('home/r/')) {
      const runId = trimmed.slice('home/r/'.length).split('/')[0];
      if (runId) {
        return { kind: 'homeRun', runId: decodeSegment(runId), shortId: knownShortId };
      }
    }
    if (trimmed.startsWith('home/list/')) {
      const category = trimmed.slice('home/list/'.length).split('/')[0];
      if (category) return { kind: 'homeList', category: decodeSegment(category) };
    }
    if (trimmed.startsWith('home/lens/')) {
      const lens = trimmed.slice('home/lens/'.length).split('/')[0];
      if (lens) return { kind: 'homeLens', lens: decodeSegment(lens) };
    }
    if (trimmed.startsWith('home/b/')) {
      const bucketId = trimmed.slice('home/b/'.length).split('/')[0];
      if (bucketId) return { kind: 'homeBucket', bucketId: decodeSegment(bucketId) };
    }
    // Legacy list path. `home/routines` would otherwise look like a routine
    // whose id is missing, or collide with `home/routine/`.
    if (trimmed === 'home/routines') return { kind: 'homeRoutines' };
    if (trimmed.startsWith('home/routine/')) {
      const bucketId = trimmed.slice('home/routine/'.length).split('/')[0];
      if (bucketId) return { kind: 'homeRoutine', bucketId: decodeSegment(bucketId) };
    }
    if (trimmed.startsWith('home/c/')) {
      const contactId = trimmed.slice('home/c/'.length).split('/')[0];
      if (contactId) return { kind: 'homeContact', contactId: decodeSegment(contactId) };
    }
    // Any other /home/** is the home itself rather than a 404 — the home is
    // where an unrecognised signed-in URL should land.
    return { kind: 'home' };
  }

  if (trimmed === 'routines') return { kind: 'homeRoutines' };
  if (trimmed === 'buckets') return { kind: 'homeBuckets' };
  if (trimmed === 'contacts') return { kind: 'homeContacts' };
  if (trimmed === 'find') return { kind: 'homeFind' };
  if (trimmed === 'activity') return { kind: 'homeActivity' };
  if (trimmed === 'design') return { kind: 'designLab' };
  if (trimmed === 'settings') return { kind: 'homeSettings', section: null };
  if (trimmed.startsWith('settings/')) {
    const name = trimmed.slice('settings/'.length).split('/')[0];
    const section = SETTINGS_SECTIONS.find((key) => key === name) ?? null;
    return { kind: 'homeSettings', section };
  }

  if (trimmed.startsWith('t/')) {
    const shortId = trimmed.slice('t/'.length).split('/')[0];
    if (shortId) return { kind: 'task', shortId };
  }

  if (trimmed.startsWith('f/')) {
    const shortId = trimmed.slice('f/'.length).split('/')[0];
    if (shortId) return { kind: 'follow', shortId };
  }

  if (trimmed.startsWith('r/')) {
    const runId = trimmed.slice('r/'.length).split('/')[0];
    if (runId) {
      let decoded: string;
      try {
        decoded = decodeURIComponent(runId);
      } catch {
        decoded = runId;
      }
      return { kind: 'runExecute', shortId: decoded };
    }
  }

  if (trimmed.startsWith('q/')) {
    const token = trimmed.slice('q/'.length).split('/')[0];
    if (token) return { kind: 'capture', token };
  }

  if (!trimmed) {
    const params = new URLSearchParams(search);
    const queryToken = params.get('token');
    if (queryToken) return { kind: 'capture', token: queryToken };
    // The root is the home. `use.x9.app` used to be a contribute-only landing,
    // which meant the app's front door showed strangers a scanner and showed
    // assignees nothing of their own; the home absorbs both jobs.
    return { kind: 'home' };
  }

  // Legacy bare token — only when the first segment is not reserved.
  if (!trimmed.includes('/')) {
    const first = trimmed.split('/')[0];
    if (first && !RESERVED_PATH_PREFIXES.has(first.toLowerCase())) {
      return { kind: 'capture', token: first };
    }
  }

  // Anything unrecognised lands on the home rather than a dead end — since the
  // home became the root it is the one page that always has something to say,
  // signed in or not.
  return { kind: 'home' };
}

/**
 * Which desktop-handoff screen to show, or `null` to admit desktop.
 *
 * The home is the only route that admits desktop (PRD D11): it is a reading
 * surface with no camera dependency, and an assignee checking their work at a
 * computer is a real case. Capture and execute keep the handoff QR.
 */
export function gateVariant(route: Route): 'capture' | 'execute' | null {
  // Only the home itself admits desktop. `/home/t/…` is the execute flow with a
  // different door on it — it still wants a camera, so it keeps the handoff.
  // `/home/sent/…` is the exception that proves the rule: same task, but a
  // contributor cannot execute it, so there is nothing to hand to a phone.
  // The list screen is reading, like the home — desktop is welcome and there is
  // no camera to demand portrait for.
  // The owner's collections read like the home does — no camera, no handoff.
  if (
    route.kind === 'home' ||
    route.kind === 'homeList' ||
    route.kind === 'homeLens' ||
    route.kind === 'homeBucket' ||
    route.kind === 'homeRoutine' ||
    route.kind === 'homeRoutines' ||
    route.kind === 'homeContact' ||
    route.kind === 'homeSent' ||
    // The footer's destinations and the header's two screens read like the
    // home does — lists and preferences, no camera, so no handoff.
    route.kind === 'homeBuckets' ||
    route.kind === 'homeContacts' ||
    route.kind === 'homeFind' ||
    route.kind === 'homeActivity' ||
    route.kind === 'homeSettings' ||
    route.kind === 'designLab'
  ) {
    return null;
  }
  return route.kind === 'task' ||
    route.kind === 'follow' ||
    route.kind === 'runExecute' ||
    route.kind === 'homeTask' ||
    route.kind === 'homeRun'
    ? 'execute'
    : 'capture';
}
